import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import type {
  ChartCategoryResponse,
  RecordResponse,
  TrendSeries,
} from '../models/ServiceTypes';

/**
 * Series ids returned by `InvestorActivityTrendByDevice`. Series 0 is the total
 * and the rest are its parts, which sum back to it.
 */
export enum LoginTrendSeriesId {
  Total = 0,
  Desktop = 1,
  Tablet = 2,
  Mobile = 3,
}

interface DateRangeArgs {
  startDate: string;
  endDate: string;
}

/**
 * Advisor-scoped client engagement data behind the Client Activity dashboard
 * widget. Every endpoint is already scoped to the signed-in advisor's book by
 * the API, so none of them takes an advisor id.
 */
export const clientActivityApi = createApi({
  reducerPath: 'clientActivityApi',
  baseQuery: waServicesQuery,
  tagTypes: ['ClientLoginTrend', 'ClientLoginFrequency', 'ClientLoginRecency'],
  endpoints: (builder) => ({
    getClientLoginTrend: builder.query<
      RecordResponse<TrendSeries>,
      DateRangeArgs
    >({
      queryFn: async ({ startDate, endDate }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/InvestorActivityTrendByDevice`,
          method: 'GET',
          params: { StartDate: startDate, EndDate: endDate },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<TrendSeries> };
      },
      providesTags: [{ type: 'ClientLoginTrend', id: 'LIST' }],
    }),

    getClientLoginFrequency: builder.query<
      ChartCategoryResponse,
      DateRangeArgs
    >({
      queryFn: async ({ startDate, endDate }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/Advisor/InvestorsLoginFrequencyChart`,
          method: 'GET',
          params: { StartDate: startDate, EndDate: endDate },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as ChartCategoryResponse };
      },
      providesTags: [{ type: 'ClientLoginFrequency', id: 'LIST' }],
    }),

    getClientLoginRecency: builder.query<ChartCategoryResponse, undefined>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/Advisor/InvestorsLoginRecencyChart`,
          method: 'GET',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as ChartCategoryResponse };
      },
      providesTags: [{ type: 'ClientLoginRecency', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetClientLoginTrendQuery,
  useGetClientLoginFrequencyQuery,
  useGetClientLoginRecencyQuery,
} = clientActivityApi;
